import React, { useState, useCallback, useEffect } from 'react';
import { IconSpinner } from '../../components';
import css from './AdminSocialPostsPage.css';

/**
 * InlineConfirm — the ONE "are you sure?" in the social admin: a two-step inline
 * confirm, never a modal and never window.confirm.
 *
 * The pattern started in the workflow list (first click arms the row, second one
 * commits) and is now the single mechanism everywhere a click is irreversible:
 * deleting a post, discarding drafts via Neu-generieren, rotating the extension
 * key. A native window.confirm cannot be styled, cannot be reached by the page's
 * own keyboard handling and, on the key rotation, was paired with a window.alert
 * that reported failure in a dialog the operator had already dismissed.
 *
 * Every handler stops propagation: these buttons live inside clickable rows (the
 * workflow row opens the editor, the post card opens the reviewer) and a confirm
 * click must never also trigger the row underneath.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.triggerLabel resting button content (text or icon)
 * @param {string} props.question the short armed prompt, e.g. "Eintrag löschen?"
 * @param {string} [props.note] one calm line of consequence, shown while armed
 * @param {Function} props.onConfirm run on the second click; may return a promise
 * @param {Function} [props.onArmedChange] notified when the armed state flips, for
 *        callers that show extra context (a warning strip) alongside the prompt
 * @param {boolean} [props.busy] the action is running — the confirm shows a spinner
 * @param {boolean} [props.disabled] the action is unavailable
 * @param {string} [props.disabledReason] WHY it is unavailable — becomes the title,
 *        so a greyed-out button is never silent
 */
const InlineConfirm = ({
    triggerLabel,
    triggerClassName = '',
    triggerAriaLabel,
    triggerTitle,
    // What the trigger says while the action runs. An icon-only trigger leaves it
    // out and shows the bare spinner; a worded button keeps its words.
    busyLabel,
    question,
    note,
    confirmLabel = 'Bestätigen',
    cancelLabel = 'Abbrechen',
    onConfirm,
    onArmedChange,
    busy = false,
    disabled = false,
    disabledReason,
    danger = false,
    className = '',
}) => {
    const [armed, setArmed] = useState(false);

    useEffect(() => {
        if (onArmedChange) onArmedChange(armed);
    }, [armed, onArmedChange]);

    // A disabled action must not stay armed underneath — the post started
    // publishing while the prompt was open.
    useEffect(() => {
        if (disabled) setArmed(false);
    }, [disabled]);

    const stop = e => {
        e.preventDefault();
        e.stopPropagation();
    };

    const confirm = useCallback(
        e => {
            stop(e);
            setArmed(false);
            const result = onConfirm();
            if (result && typeof result.catch === 'function') {
                // Errors surface in the store's banner; the button stays quiet.
                result.catch(() => {});
            }
        },
        [onConfirm]
    );

    if (!armed) {
        return (
            <button
                type="button"
                className={`${triggerClassName} ${className}`}
                onClick={e => {
                    stop(e);
                    setArmed(true);
                }}
                onKeyDown={e => e.stopPropagation()}
                disabled={disabled || busy}
                aria-label={triggerAriaLabel}
                title={disabled && disabledReason ? disabledReason : triggerTitle}
            >
                {busy && busyLabel ? (
                    <span className={css.inlineConfirmBusy}>
                        <IconSpinner />
                        <span>{busyLabel}</span>
                    </span>
                ) : busy ? (
                    <IconSpinner />
                ) : (
                    triggerLabel
                )}
            </button>
        );
    }

    return (
        <span
            className={`${css.inlineConfirm} ${className}`}
            onKeyDown={e => {
                e.stopPropagation();
                if (e.key === 'Escape') setArmed(false);
            }}
        >
            <span className={css.inlineConfirmPrompt}>
                <span className={css.inlineConfirmQuestion}>{question}</span>
                {note ? <span className={css.inlineConfirmNote}>{note}</span> : null}
            </span>
            <button
                type="button"
                className={danger ? css.inlineConfirmDanger : css.inlineConfirmOk}
                onClick={confirm}
                disabled={busy}
                autoFocus
            >
                {busy ? <IconSpinner /> : confirmLabel}
            </button>
            <button
                type="button"
                className={css.inlineConfirmCancel}
                onClick={e => {
                    stop(e);
                    setArmed(false);
                }}
                disabled={busy}
            >
                {cancelLabel}
            </button>
        </span>
    );
};

export default InlineConfirm;
